import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useQuery } from "@tanstack/react-query";

interface DevMockContextType {
  /** True when logged-in user is developer */
  isDev: boolean;
  /** True when dev is browsing a page outside their natural role */
  isMockMode: boolean;
}

const DevMockContext = createContext<DevMockContextType>({
  isDev: false,
  isMockMode: false,
});

export function useDevMock() {
  return useContext(DevMockContext);
}

export function DevMockProvider({ children }: { children: React.ReactNode }) {
  const { session, getUserRole } = useAuth();

  const { data: userRole } = useQuery({
    queryKey: ["dev-mock-role", session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return null;
      return getUserRole(session.user.id);
    },
    enabled: !!session?.user?.id,
    staleTime: Infinity,
  });

  const isDev = userRole === "developer";

  const value = useMemo(
    () => ({
      isDev,
      isMockMode: isDev, // When dev, always enable mock fallback
    }),
    [isDev]
  );

  return (
    <DevMockContext.Provider value={value}>{children}</DevMockContext.Provider>
  );
}
